import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "@legal-500/legal500-design-system";
import { researchCms, fetchAllEditionBpas } from "@/api/researchCms.service";
import { usePermissions } from "@/hooks/usePermissions";
import { TaxonomyBpaTierSelector } from "@/components/ResearchCms/TaxonomyBpaTierSelector/TaxonomyBpaTierSelector";
import {
  splitHubUrl,
  splitNameProblem,
  splitRankingTables,
  type RankingListShape,
  type SplitCardInput,
} from "../../utils/bpaSplit";
import { TaxonomyPath } from "./TaxonomyPath";
import styles from "./CmsBpaSplitDialog.module.css";
import shared from "../BpaImport/importShared.module.css";

const CREATE_NEEDS_PERMISSION_REASON = "Creating practice areas needs create permission.";

type CmsBpaSplitDialogProps = {
  editionId: string;
  bpaId: string;
  editionName: string;
  onClose: () => void;
};

type NewPracticeArea = { name: string; taxonomyBpaId: string | null };

function hasFailed(query: { isError: boolean; isFetching: boolean }) {
  return query.isError && !query.isFetching;
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : "Something went wrong";
}

export function CmsBpaSplitDialog({
  editionId,
  bpaId,
  editionName,
  onClose,
}: CmsBpaSplitDialogProps) {
  const titleId = useId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission("staff.research.create");

  const [first, setFirst] = useState<NewPracticeArea>({ name: "", taxonomyBpaId: null });
  const [second, setSecond] = useState<NewPracticeArea>({ name: "", taxonomyBpaId: null });
  const [problem, setProblem] = useState<string | null>(null);
  const [createdFirst, setCreatedFirst] = useState<SplitCardInput | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const editionBpasQuery = useQuery({
    queryKey: ["research-split-dialog-edition-bpas", editionId],
    queryFn: () => fetchAllEditionBpas({ editionId, includeArchived: true }),
  });
  const tablesQuery = useQuery({
    queryKey: ["research-split-dialog-ranking-tables", bpaId, editionId],
    queryFn: () => researchCms.getRankingTables({ bpaId, editionId }),
  });

  const editionBpas = editionBpasQuery.data ?? [];
  const original = editionBpas.find(item => item.bpaId === bpaId) ?? null;
  const originalLists: RankingListShape[] = (tablesQuery.data?.rankingTables ?? []).map(table => ({
    name: table.name,
    subtype: table.subtype,
    isTiered: table.isTiered,
    submissionType: table.submissionType ?? null,
    refereeType: table.refereeType ?? null,
  }));
  const ready = original !== null && tablesQuery.data !== undefined;
  const loadFailed = hasFailed(editionBpasQuery) || hasFailed(tablesQuery);

  function retryLoad() {
    if (hasFailed(editionBpasQuery)) editionBpasQuery.refetch();
    if (hasFailed(tablesQuery)) tablesQuery.refetch();
  }

  const createBoth = useMutation({
    mutationFn: createBothAreas,
    onSuccess: openHub,
  });
  const creating = createBoth.isPending;
  const createError = createBoth.isError ? errorText(createBoth.error) : null;

  function requestClose() {
    if (!creating) onClose();
  }

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !creating) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, creating]);

  async function createOne(area: NewPracticeArea): Promise<SplitCardInput> {
    if (!original) throw new Error("The practice area is not loaded");
    const result = await researchCms.createResearchBpa({
      name: area.name.trim(),
      rankingEntityId: original.rankingEntityId,
      practiceGroupId: original.practiceGroupId ?? null,
      editionId,
      taxonomyBpaId: area.taxonomyBpaId,
      assignedResearcherId: original.assignedResearcherId ?? null,
      assignedEditorId: original.assignedEditorId ?? null,
      sourceEditionBpaId: original.id,
      rankingTables: splitRankingTables(originalLists, original.bpaName, area.name.trim()),
    });
    return {
      editionBpaId: result.editionBpa.id,
      bpaId: result.researchBpa.id as string,
      name: area.name.trim(),
    };
  }

  function openHub(cards: SplitCardInput[]) {
    queryClient.invalidateQueries({ queryKey: ["research-edition-bpas"] });
    queryClient.invalidateQueries({ queryKey: ["research-split-halves"] });
    navigate(splitHubUrl(editionId, bpaId, cards));
  }

  async function createBothAreas(): Promise<SplitCardInput[]> {
    let firstCard = createdFirst;
    if (!firstCard) {
      try {
        firstCard = await createOne(first);
        setCreatedFirst(firstCard);
      } catch (err) {
        throw new Error(`Creating ${first.name.trim()} failed: ${errorText(err)}`);
      }
    }
    try {
      const secondCard = await createOne(second);
      return [firstCard, secondCard];
    } catch (err) {
      throw new Error(
        `${firstCard.name} was created. Creating ${second.name.trim()} failed: ${errorText(err)}`,
      );
    }
  }

  function handleCreate() {
    if (!original || creating) return;
    const createdName = createdFirst?.name?.trim().toLowerCase();
    const found = splitNameProblem(
      first.name,
      second.name,
      editionBpas
        .map(item => item.bpaName)
        .filter(name => name.trim().toLowerCase() !== createdName),
    );
    setProblem(found);
    if (found) return;
    createBoth.mutate();
  }

  const createLabel = createdFirst ? `Retry ${second.name.trim()}` : "Create both";
  const disabledReason = !canCreate
    ? CREATE_NEEDS_PERMISSION_REASON
    : loadFailed
      ? "The practice area could not be loaded."
      : !ready
        ? "Loading the practice area."
        : null;
  const tableNames = originalLists.map(list => list.name);

  return createPortal(
    <div
      className={styles.overlay}
      onClick={event => event.target === event.currentTarget && requestClose()}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className={styles.body}>
          <div className={styles.header}>
            <div className={styles.headerText}>
              <h2 id={titleId} className={styles.title}>
                Split {original?.bpaName ?? "practice area"}
              </h2>
              <p className={styles.description}>
                Create the two practice areas its organisations will move into.
              </p>
            </div>
            <button
              type="button"
              className={styles.closeButton}
              aria-label="Close"
              disabled={creating}
              onClick={requestClose}
            >
              <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
                <path
                  d="M3 3l10 10M13 3L3 13"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </button>
          </div>

          <div className={styles.flow}>
            <span className={styles.originalPill}>The original</span>
            <span className={styles.flowTo}>
              <span aria-hidden="true">→</span>
              <span>2 new practice areas</span>
            </span>
          </div>

          <div className={styles.fields}>
            {[
              {
                legend: "First practice area",
                badge: "A",
                area: first,
                set: setFirst,
                locked: !!createdFirst,
              },
              {
                legend: "Second practice area",
                badge: "B",
                area: second,
                set: setSecond,
                locked: false,
              },
            ].map(({ legend, badge, area, set, locked }) => {
              const fieldId = `${titleId}-${legend.replaceAll(" ", "-")}`;
              return (
                <div
                  key={legend}
                  role="group"
                  aria-labelledby={`${fieldId}-heading`}
                  className={styles.fieldset}
                >
                  <div id={`${fieldId}-heading`} className={styles.legend}>
                    <span className={styles.badge} aria-hidden="true">
                      {badge}
                    </span>
                    {legend}
                  </div>
                  <Input
                    id={`${fieldId}-name`}
                    label="Name"
                    fullWidth
                    value={area.name}
                    disabled={locked || creating}
                    onChange={event => set({ ...area, name: event.target.value })}
                  />
                  <span id={`${fieldId}-taxonomy`} className={styles.label}>
                    Taxonomy BPA
                  </span>
                  <div role="group" aria-labelledby={`${fieldId}-taxonomy`}>
                    <TaxonomyBpaTierSelector
                      value={area.taxonomyBpaId}
                      onChange={taxonomyBpaId => set({ ...area, taxonomyBpaId })}
                      disabled={locked || creating}
                      variant="dropdown"
                    />
                  </div>
                  <TaxonomyPath taxonomyBpaId={area.taxonomyBpaId} />
                </div>
              );
            })}
          </div>

          <section className={styles.copied} aria-label={`Copied from ${original?.bpaName ?? ""}`}>
            <h3 className={styles.copiedTitle}>Copied from the original</h3>
            <div className={styles.copiedRows}>
              <div className={styles.copiedRow}>
                <span className={styles.copiedTerm}>Edition</span>
                <p className={styles.copiedValue}>{editionName}</p>
              </div>
              <div className={styles.copiedRow}>
                <span className={styles.copiedTerm}>Ranking entity</span>
                <p className={styles.copiedValue}>{original?.rankingEntityName ?? ""}</p>
              </div>
              <div className={styles.copiedRow}>
                <span className={styles.copiedTerm}>Ranking tables</span>
                {tablesQuery.data && tableNames.length > 0 ? (
                  <ul className={styles.tags}>
                    {tableNames.map((name, index) => (
                      <li key={`${name}-${index}`} className={styles.tag}>
                        {name}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={styles.copiedValue}>
                    {tablesQuery.data
                      ? "None"
                      : hasFailed(tablesQuery)
                        ? "Could not load"
                        : "Loading"}
                  </p>
                )}
              </div>
            </div>
          </section>

          {loadFailed && (
            <div className={styles.problem} role="alert">
              <span>Could not load the practice area.</span>
              <Button variant="outlined" color="default" size="small" onClick={retryLoad}>
                Retry
              </Button>
            </div>
          )}
          {problem && (
            <p className={styles.problem} role="alert">
              {problem}
            </p>
          )}
          {createError && (
            <p className={styles.problem} role="alert">
              {createError}
            </p>
          )}
        </div>

        <div className={styles.footer}>
          <Button
            variant="link"
            color="primary"
            className={styles.chooseLink}
            disabled={creating}
            onClick={() => navigate(splitHubUrl(editionId, bpaId, []))}
          >
            Already created them? Choose practice areas
          </Button>
          <div className={styles.footerActions}>
            {disabledReason && (
              <span id="split-create-reason" className={styles.reason}>
                {disabledReason}
              </span>
            )}
            <Button
              className={shared.compactButton}
              variant="outlined"
              color="default"
              disabled={creating}
              onClick={requestClose}
            >
              Cancel
            </Button>
            <Button
              className={shared.compactButton}
              variant="contained"
              color="primary"
              disabled={disabledReason !== null || creating}
              aria-describedby={disabledReason ? "split-create-reason" : undefined}
              onClick={handleCreate}
            >
              {creating ? "Creating..." : createLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
